import React, { useState, useRef, useEffect } from "react";
import { ChevronDown, Loader2, ShieldOff } from "lucide-react";
import { LEAD_DISPOSITIONS, leadDispositionStyle } from "../lib/leadDispositions";
import { api } from "../lib/api";
import { toast } from "sonner";

/**
 * One-tap disposition pill. Click → dropdown of 12 statuses → PATCHes /api/prospects/{id}.
 * Calls onUpdate(updatedProspect) so the parent can update its row state without refetch.
 *
 * Visual: color-coded pill (per `leadDispositionStyle`), small dot + label + chevron.
 *         "DNC" gets a tiny shield icon to make the hard-stop unmistakable.
 */
export default function LeadDispositionBadge({ prospectId, value, onUpdate, compact = false }) {
  const current = value || "New Lead";
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const ref = useRef(null);

  const style = leadDispositionStyle(current);

  useEffect(() => {
    const onDocClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open]);

  const pick = async (next) => {
    if (next === current) {
      setOpen(false);
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.patch(`/prospects/${prospectId}`, { disposition: next });
      toast.success(`Status → ${next}${next === "DNC" ? " · phone added to DNC list" : ""}`);
      onUpdate?.(data);
      setOpen(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update status");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div ref={ref} className="relative inline-flex" data-testid={`lead-disposition-badge-${prospectId}`}>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setOpen((v) => !v);
        }}
        disabled={saving}
        data-testid={`lead-disposition-trigger-${prospectId}`}
        className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11px] font-mono uppercase tracking-wider
          ring-1 ${style.bg} ${style.text} ${style.ring}
          transition-colors hover:brightness-125 disabled:opacity-50
          ${compact ? "" : "min-w-[140px] justify-between"}`}
      >
        <span className="inline-flex items-center gap-1.5 truncate">
          {current === "DNC" ? (
            <ShieldOff className="w-3 h-3 shrink-0" />
          ) : (
            <span className={`w-1.5 h-1.5 rounded-full ${style.dot}`} />
          )}
          <span className="truncate">{current}</span>
        </span>
        {saving ? (
          <Loader2 className="w-3 h-3 animate-spin shrink-0" />
        ) : (
          <ChevronDown className="w-3 h-3 opacity-60 shrink-0" />
        )}
      </button>

      {open && (
        <div
          data-testid={`lead-disposition-menu-${prospectId}`}
          className="absolute z-50 top-full mt-1 left-0 w-[200px] bg-[#121214] border border-white/10 rounded-lg shadow-2xl py-1 max-h-[360px] overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
        >
          {LEAD_DISPOSITIONS.map((d) => {
            const s = leadDispositionStyle(d);
            const active = d === current;
            return (
              <button
                key={d}
                onClick={() => pick(d)}
                data-testid={`lead-disposition-option-${d.replace(/[^a-zA-Z0-9]/g, "-")}-${prospectId}`}
                className={`w-full flex items-center gap-2 px-3 py-2 text-xs font-mono uppercase tracking-wider text-left
                  hover:bg-white/5 transition-colors ${active ? "bg-white/[0.03]" : ""}`}
              >
                {d === "DNC" ? (
                  <ShieldOff className={`w-3 h-3 shrink-0 ${s.text}`} />
                ) : (
                  <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${s.dot}`} />
                )}
                <span className={`flex-1 truncate ${s.text}`}>{d}</span>
                {active && <span className="text-[#FFB000] text-[10px]">●</span>}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
